'use client';
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

const REVIEWS = [
  { id: 'rev-001', name: 'Erik Solberg', location: 'Oslo', rating: 5, date: '12.06.2026', text: 'Rented a Toyota RAV4 for 10 days. Pickup was fast, car was spotless and well-maintained. The weekly discount made a real difference. Will use DIN AUTO every time.' },
  { id: 'rev-002', name: 'Anna Lindqvist', location: 'Stockholm (visiting)', rating: 5, date: '28.05.2026', text: 'As a Swede visiting Oslo I was a bit worried about the process, but everything was smooth. Staff spoke excellent English and the Volvo XC60 was perfect for our mountain trip.' },
  { id: 'rev-003', name: 'Bjørn Haugen', location: 'Drammen', rating: 4, date: '15.05.2026', text: 'Good selection of cars at honest prices. The VW Golf I rented was clean and comfortable. Minus one star only because the office was slightly hard to find the first time.' },
  { id: 'rev-004', name: 'Maria Santos', location: 'Lisbon (visiting)', rating: 5, date: '03.05.2026', text: 'Booked a BMW 3 Series for a week. Online reservation was simple, no surprises at pickup. The car was immaculate. Highly recommend to anyone visiting Oslo.' },
  { id: 'rev-005', name: 'Håkon Dahl', location: 'Bergen', rating: 5, date: '20.04.2026', text: 'Used DIN AUTO for a business trip. Mercedes C-Class, clean and professional. The 15% discount on 14+ days saved us significantly. Invoicing was also straightforward.' },
  { id: 'rev-006', name: 'Lena Kristiansen', location: 'Trondheim', rating: 5, date: '08.04.2026', text: 'Rented a Kia Sportage with a child seat for a family holiday. Everything worked perfectly. The kids loved the car and the add-on was worth every krone.' },
  { id: 'rev-007', name: 'Thomas Meier', location: 'Munich (visiting)', rating: 4, date: '25.03.2026', text: 'Solid car rental experience in Oslo. The Hyundai Tucson hybrid was comfortable and economical. Good value compared to the big international chains.' },
  { id: 'rev-008', name: 'Ingeborg Nygaard', location: 'Stavanger', rating: 5, date: '14.03.2026', text: 'Third time renting here. Always the same reliable service. The online booking form is clear and the live price calculator is genuinely helpful for planning costs.' },
];

export default function ReviewsSection() {
  const [page, setPage] = useState(0);
  const perPage = 3;
  const totalPages = Math.ceil(REVIEWS?.length / perPage);
  const visible = REVIEWS?.slice(page * perPage, page * perPage + perPage);

  return (
    <section className="py-24 bg-foreground text-white">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-primary" />
              <span className="text-xs font-bold uppercase tracking-widest text-primary/80">Customer Reviews</span>
            </div>
            <h2 className="font-black text-5xl leading-none text-white" style={{ fontWeight: 900 }}>
              Real customers,<br />real opinions.
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-2 border border-white/20 text-white hover:bg-white/10 disabled:opacity-30 transition-colors"
              aria-label="Previous reviews"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-white/40 text-sm tabular-nums">{page + 1} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page === totalPages - 1}
              className="p-2 border border-white/20 text-white hover:bg-white/10 disabled:opacity-30 transition-colors"
              aria-label="Next reviews"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {visible?.map((review) => (
            <div key={review?.id} className="bg-white/5 border border-white/10 p-6 hover:bg-white/8 transition-colors">
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: 5 })?.map((_, i) => (
                  <Star
                    key={`star-${review?.id}-${i}`}
                    size={14}
                    className={i < review?.rating ? 'text-primary fill-primary' : 'text-white/20'}
                  />
                ))}
              </div>
              <p className="text-white/75 text-sm leading-relaxed mb-5">&ldquo;{review?.text}&rdquo;</p>
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div>
                  <p className="font-700 text-sm text-white">{review?.name}</p>
                  <p className="text-white/40 text-xs">{review?.location}</p>
                </div>
                <p className="text-white/30 text-xs">{review?.date}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Overall rating */}
        <div className="mt-10 flex items-center gap-6 pt-8 border-t border-white/10">
          <div className="text-center">
            <p className="text-5xl font-black text-primary tabular-nums" style={{ fontWeight: 900 }}>4.9</p>
            <div className="flex gap-0.5 justify-center mt-1">
              {Array.from({ length: 5 })?.map((_, i) => (
                <Star key={`overall-star-${i}`} size={12} className="text-primary fill-primary" />
              ))}
            </div>
          </div>
          <div>
            <p className="text-white font-700 text-lg">Excellent rating</p>
            <p className="text-white/45 text-sm">Based on 214 verified customer reviews</p>
          </div>
        </div>
      </div>
    </section>
  );
}